import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal } from '@givebutter/util';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { faClone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { duplicateEngageMessage, getMessageStats } from 'api/engage';
import { StatusBadge } from 'assets/js/pages/engage/components/StatusBadge';
import * as DropdownMenu from 'components/dropdown-menu';
import { useReactTableState } from 'components/table';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { EmptyData } from 'components/util';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentEngageMessage } from 'customHooks/useCurrentEngageMessage';
import { useIsPending } from 'customHooks/useIsPending';
import { accountQueryKeys } from 'queries/account-query-keys';
import { EngageDelivery } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<EngageDelivery>();

const EngageMetricsRecipientsTable = () => {
    const { accountId } = useCurrentAccount();
    const { data: message } = useCurrentEngageMessage();
    const toast = useToast();
    const queryClient = useQueryClient();
    const history = useHistory();

    const { pagination, setPagination } = useReactTableState({
        pageSize: 10,
    });

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
        },
        500,
    );

    const { data: messageStats, isLoading: isStatsLoading } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageStatsWithFilters(accountId, message.id, params),
        queryFn: () => getMessageStats(accountId, message.id, params),
    });

    const { mutate: duplicateMessage } = useMutation({
        mutationFn: (messageId: number) => duplicateEngageMessage(accountId, messageId),
        onSuccess: message => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessages(accountId),
            });
            history.push(`/accounts/${accountId}/engage/${message.channel}/${message.id}/edit`);
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data?.message ?? 'Failed to duplicate message');
        },
    });

    const columns = useMemo(() => {
        const { channel } = message;

        return [
            columnHelper.display({
                id: 'name',
                header: 'Name',
                cell: info => {
                    const { first_name, last_name } = info.row.original.deliverable || {};
                    const name = first_name ? `${first_name} ${last_name}` : 'N/A';

                    return <p>{name}</p>;
                },
            }),
            columnHelper.display({
                id: channel === 'email' ? 'email' : 'phone',
                header: channel === 'email' ? 'Email address' : 'Phone number',
                meta: {
                    ignoreLink: true,
                },
                cell: info => {
                    const { recipient } = info.row.original || {};

                    return (
                        <div className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">
                            {recipient ? (
                                <a
                                    className="flex font-normal text-black"
                                    href={
                                        channel === 'email'
                                            ? `mailto:${recipient}`
                                            : `tel:${recipient}`
                                    }
                                >
                                    {recipient}
                                </a>
                            ) : (
                                <EmptyData />
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: 'status',
                header: 'Status',
                cell: info => {
                    return <StatusBadge row={info.row.original} channel={message.channel} />;
                },
            }),
            columnHelper.display({
                id: 'revenue_generated',
                header: 'Contribution',
                cell: info => {
                    return (
                        <div className="font-semibold">
                            <CurrencySymbol />
                            {maskDecimal(info.row.original.revenue_generated / 100) || 0}
                        </div>
                    );
                },
            }),
        ];
    }, [message]);

    const table = useReactTable({
        data: messageStats?.data ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        pageCount: messageStats?.meta.last_page ?? -1,
        state: {
            pagination,
        },
        meta: {
            to: cell => `/accounts/${accountId}/contacts/${cell.row.original.deliverable.id}`,
        },
        onPaginationChange: setPagination,
        enableSorting: false,
        manualPagination: true,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, messageStats);

    return (
        <div className="flex flex-col gap-4">
            <div className="block text-lg font-medium">Recipients</div>
            <Table.Root table={table} variant="bordered" isLoading={isPending || isStatsLoading}>
                <Box>
                    <div className="md:flex-nowrap' flex flex-wrap items-center justify-end gap-4 border-b border-b-gray-100 p-4">
                        <div className="flex items-center gap-2">
                            <div className="font-light">{messageStats?.meta.total} Results</div>
                            <DropdownMenu.Root>
                                <DropdownMenu.DropdownMenuTrigger>
                                    <Button variant="outline" color="neutral">
                                        Actions
                                        <FontAwesomeIcon icon={faChevronDown} />
                                    </Button>
                                </DropdownMenu.DropdownMenuTrigger>
                                <DropdownMenu.Content align="end">
                                    <DropdownMenu.Item onClick={() => duplicateMessage(message.id)}>
                                        <div className="flex items-center gap-2">
                                            <FontAwesomeIcon icon={faClone} /> Duplicate & edit
                                        </div>
                                    </DropdownMenu.Item>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </div>
                    </div>
                    <Table.Content>
                        <Table.Header />
                        <Table.Body />
                    </Table.Content>
                    <Table.Pagination>
                        <Table.PaginationPageNavigation />
                    </Table.Pagination>
                    {!isPending && (
                        <Table.Empty variant={'secondary'}>
                            <Table.EmptyIcon />
                            <Table.EmptyTitle>No contacts found</Table.EmptyTitle>
                        </Table.Empty>
                    )}
                </Box>
            </Table.Root>
        </div>
    );
};

export { EngageMetricsRecipientsTable };
